import React, { useState, useMemo } from 'react';
import {
  Box, Typography, Slider, ToggleButton, ToggleButtonGroup,
  Accordion, AccordionSummary, AccordionDetails,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { Bar } from 'react-chartjs-2';
import {
  Chart as ChartJS, CategoryScale, LinearScale, BarElement, Tooltip, Legend,
} from 'chart.js';
import { useTranslation } from 'react-i18next';
import { projectGrowth, rrspAfterTax, TAX_DATA } from '../../data/taxBrackets';

ChartJS.register(CategoryScale, LinearScale, BarElement, Tooltip, Legend);

const RETURN_PRESETS = [0.04, 0.06, 0.08];

const ProjectionChart = ({ contribution, marginalRate, frequency = 'monthly' }) => {
  const { t } = useTranslation();
  const p = 'calc.projection';

  const [returnRate, setReturnRate] = useState(0.06);
  const monthlyAmount = frequency === 'annual' ? (contribution || 0) / 12 : (contribution || 0);

  const [sliderValue, setSliderValue] = useState(monthlyAmount || 500);
  const monthly = sliderValue;

  const labels = [t('TFSA'), t('RRSP'), t('FHSA')];
  const returnLabels = {
    0.04: t(`${p}.conservative`),
    0.06: t(`${p}.moderate`),
    0.08: t(`${p}.aggressive`),
  };

  const projections = useMemo(() => {
    const tfsa10 = projectGrowth(monthly, 10, returnRate);
    const rrsp10 = projectGrowth(monthly, 10, returnRate);
    const rrsp10Tax = rrspAfterTax(rrsp10, marginalRate);
    // FHSA: max lifetime $40K, max annual $8K → max ~5 years of contributions
    const fhsaMonths = Math.min(monthly > 0 ? Math.ceil(TAX_DATA.fhsaLifetime / (monthly * 12)) : 5, 5);
    const fhsa = projectGrowth(Math.min(monthly, TAX_DATA.fhsaAnnual / 12), fhsaMonths, returnRate);

    return { tfsa10, rrsp10, rrsp10Tax, fhsa };
  }, [monthly, returnRate, marginalRate]);

  const chartData = {
    labels: ['TFSA', 'RRSP', 'FHSA'],
    datasets: [
      {
        label: t(`${p}.accumulated`),
        data: [projections.tfsa10, projections.rrsp10, projections.fhsa],
        backgroundColor: ['#2E7D32', '#1565C0', '#F9A825'],
        borderRadius: 6,
      },
      {
        label: t(`${p}.afterTax`),
        data: [projections.tfsa10, projections.rrsp10Tax, projections.fhsa],
        backgroundColor: ['#2E7D3280', '#1565C080', '#F9A82580'],
        borderRadius: 6,
      },
    ],
  };

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { position: 'bottom', labels: { font: { size: 12 }, padding: 16 } },
      tooltip: {
        callbacks: {
          label: (ctx) => `${ctx.dataset.label}: $${ctx.raw.toLocaleString()}`,
        },
      },
    },
    scales: {
      y: {
        ticks: { callback: (v) => `$${(v / 1000).toFixed(0)}K` },
        grid: { color: '#EEF0F340' },
      },
      x: { grid: { display: false } },
    },
  };

  return (
    <Box>
      <Typography variant="h6" sx={{ fontWeight: 700, color: '#1A1A1A', mb: 2, fontSize: '1.1rem' }}>
        {t(`${p}.title`)}
      </Typography>

      {/* Monthly slider */}
      <Box sx={{ mb: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
          <Typography sx={{ fontSize: 13, color: '#5F6B7A' }}>{t(`${p}.monthly`)}</Typography>
          <Typography sx={{ fontSize: 14, fontWeight: 700, color: '#1E3A5F', fontVariantNumeric: 'tabular-nums' }}>
            ${sliderValue.toLocaleString()}/mo
          </Typography>
        </Box>
        <Slider
          value={sliderValue}
          onChange={(_, v) => setSliderValue(v)}
          min={50} max={2000} step={50}
          sx={{
            color: '#1E3A5F',
            '& .MuiSlider-thumb': { width: 20, height: 20 },
          }}
        />
      </Box>

      {/* Return preset */}
      <Box sx={{ mb: 3 }}>
        <Typography sx={{ fontSize: 13, color: '#5F6B7A', mb: 1 }}>{t(`${p}.return`)}</Typography>
        <ToggleButtonGroup
          value={returnRate}
          exclusive
          onChange={(_, v) => v !== null && setReturnRate(v)}
          size="small"
          sx={{ '& .MuiToggleButton-root': { textTransform: 'none', fontSize: 13, px: 2 } }}
        >
          {RETURN_PRESETS.map((r) => (
            <ToggleButton key={r} value={r}>{returnLabels[r]}</ToggleButton>
          ))}
        </ToggleButtonGroup>
      </Box>

      {/* Chart */}
      <Box sx={{ height: 300, mb: 2, position: 'relative' }}>
        <Typography
          sx={{
            position: 'absolute', top: 8, right: 12, zIndex: 2,
            fontSize: 12, fontWeight: 800, color: '#F9A825',
            bgcolor: '#FFF8E1', px: 1, py: 0.25, borderRadius: 1,
          }}
        >
          {t(`${p}.estimateLabel`)}
        </Typography>
        <Bar data={chartData} options={chartOptions} />
      </Box>

      {/* Numbers summary */}
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, mb: 2 }}>
        <SummaryRow
          label="TFSA" sub={t(`${p}.tenYears`)}
          value={`$${projections.tfsa10.toLocaleString()}`}
          note={t(`${p}.allYours`)}
          color="#2E7D32"
        />
        <SummaryRow
          label="RRSP" sub={t(`${p}.tenYears`)}
          value={`$${projections.rrsp10.toLocaleString()}`}
          note={`~$${projections.rrsp10Tax.toLocaleString()} ${t(`${p}.afterTaxLabel`)}`}
          color="#1565C0"
        />
        <SummaryRow
          label="FHSA" sub={t(`${p}.fhsaMax`)}
          value={`$${projections.fhsa.toLocaleString()}`}
          note={`$${TAX_DATA.fhsaLifetime.toLocaleString()} max`}
          color="#E65100"
        />
      </Box>

      {/* Assumptions accordion */}
      <Accordion
        disableGutters elevation={0}
        sx={{ bgcolor: '#FAFBFD', border: '1px solid #EEF0F3', borderRadius: '12px !important', '&:before': { display: 'none' } }}
      >
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography sx={{ fontSize: 13, fontWeight: 600, color: '#5F6B7A' }}>
            {t(`${p}.assumptions`)}
          </Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Box component="ul" sx={{ m: 0, pl: 2.5, '& li': { fontSize: 13, color: '#5F6B7A', mb: 0.5 } }}>
            <li>{t(`${p}.a1`, { rate: `${(returnRate * 100).toFixed(0)}%` })}</li>
            <li>{t(`${p}.a2`)}</li>
            <li>{t(`${p}.a3`)}</li>
            <li>{t(`${p}.a4`)}</li>
            <li>{t(`${p}.a5`)}</li>
          </Box>
        </AccordionDetails>
      </Accordion>
    </Box>
  );
};

const SummaryRow = ({ label, sub, value, note, color }) => (
  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', py: 1, px: 2, bgcolor: '#FAFBFD', borderRadius: 2 }}>
    <Box>
      <Typography sx={{ fontSize: 15, fontWeight: 800, color }}>{label}</Typography>
      <Typography sx={{ fontSize: 12, color: '#5F6B7A' }}>{sub}</Typography>
    </Box>
    <Box sx={{ textAlign: 'right' }}>
      <Typography sx={{ fontSize: 18, fontWeight: 800, color: '#1A1A1A', fontVariantNumeric: 'tabular-nums' }}>{value}</Typography>
      {note && <Typography sx={{ fontSize: 12, color: '#5F6B7A' }}>{note}</Typography>}
    </Box>
  </Box>
);

export default ProjectionChart;
